import type { IntegrationProvider, IntegrationType } from '@yawi/types';

export interface ProviderInfo {
  provider: IntegrationProvider;
  name: string;
  description: string;
}

export interface ConfigField {
  key: string;
  label: string;
  type: 'text' | 'password' | 'number' | 'boolean';
  placeholder: string;
  required: boolean;
  helpText?: string;
}

export const providersByType: Record<IntegrationType, ProviderInfo[]> = {
  EMAIL: [
    { provider: 'SENDGRID', name: 'SendGrid', description: 'SendGrid API - Escalable y confiable' },
    { provider: 'BREVO', name: 'Brevo', description: 'Brevo (ex Sendinblue) - Marketing y transaccional' },
    { provider: 'MAILERSEND', name: 'MailerSend', description: 'MailerSend - Simple y económico' },
    { provider: 'SMTP', name: 'SMTP', description: 'SMTP genérico - Compatible con cualquier servidor' },
  ],
  SMS: [
    { provider: 'TWILIO', name: 'Twilio SMS', description: 'Twilio - Líder en comunicaciones' },
  ],
  WHATSAPP: [
    { provider: 'META_WHATSAPP', name: 'Meta Cloud API', description: 'API oficial de WhatsApp Business' },
    { provider: 'TWILIO_WHATSAPP', name: 'Twilio WhatsApp', description: 'WhatsApp via Twilio' },
  ],
  PUSH: [
    { provider: 'FIREBASE', name: 'Firebase', description: 'Firebase Cloud Messaging (FCM)' },
  ],
  PAYMENT: [
    { provider: 'STRIPE', name: 'Stripe', description: 'Stripe - Pagos globales' },
    { provider: 'MERCADOPAGO', name: 'MercadoPago', description: 'MercadoPago - LATAM' },
    { provider: 'CONEKTA', name: 'Conekta', description: 'Conekta - México' },
  ],
  ACCESS: [
    { provider: 'HIKCENTRAL', name: 'HikCentral', description: 'Hikvision - Control de acceso y CCTV' },
    { provider: 'DAHUA', name: 'Dahua DSS', description: 'Dahua - Videovigilancia y control de acceso' },
    { provider: 'ZKTECO', name: 'ZKTeco', description: 'ZKTeco - Control biométrico' },
  ],
  STORAGE: [
    { provider: 'AWS_S3', name: 'Amazon S3', description: 'AWS S3 - Almacenamiento en la nube' },
  ],
};

export const configFieldsByProvider: Record<string, ConfigField[]> = {
  // Email Providers
  SENDGRID: [
    { key: 'apiKey', label: 'API Key', type: 'password', placeholder: 'SG.xxxxxx', required: true },
    { key: 'fromEmail', label: 'From Email', type: 'text', placeholder: 'noreply@tudominio.com', required: true },
    { key: 'fromName', label: 'From Name', type: 'text', placeholder: 'Tu App', required: true },
  ],
  BREVO: [
    { key: 'apiKey', label: 'API Key', type: 'password', placeholder: 'xkeysib-xxxxxx', required: true },
    { key: 'fromEmail', label: 'From Email', type: 'text', placeholder: 'noreply@tudominio.com', required: true },
    { key: 'fromName', label: 'From Name', type: 'text', placeholder: 'Tu App', required: true },
  ],
  MAILERSEND: [
    { key: 'apiKey', label: 'API Key', type: 'password', placeholder: 'mlsn.xxxxxx', required: true },
    { key: 'fromEmail', label: 'From Email', type: 'text', placeholder: 'noreply@tudominio.com', required: true },
    { key: 'fromName', label: 'From Name', type: 'text', placeholder: 'Tu App', required: true },
  ],
  SMTP: [
    { key: 'host', label: 'SMTP Host', type: 'text', placeholder: 'smtp.gmail.com', required: true },
    { key: 'port', label: 'Port', type: 'number', placeholder: '587', required: true },
    { key: 'secure', label: 'SSL/TLS', type: 'boolean', placeholder: '', required: true },
    { key: 'user', label: 'Username', type: 'text', placeholder: 'tu-usuario@gmail.com', required: true },
    { key: 'password', label: 'Password', type: 'password', placeholder: 'tu-app-password', required: true },
    { key: 'fromEmail', label: 'From Email', type: 'text', placeholder: 'noreply@tudominio.com', required: true },
    { key: 'fromName', label: 'From Name', type: 'text', placeholder: 'Tu App', required: true },
  ],
  // SMS Provider
  TWILIO: [
    { key: 'accountSid', label: 'Account SID', type: 'text', placeholder: 'ACxxxxxx', required: true },
    { key: 'authToken', label: 'Auth Token', type: 'password', placeholder: 'Tu auth token', required: true },
    { key: 'fromNumber', label: 'From Number', type: 'text', placeholder: '+1234567890', required: true },
  ],
  // WhatsApp Providers
  META_WHATSAPP: [
    { key: 'apiToken', label: 'API Token', type: 'password', placeholder: 'EAAxxxxxxxx', required: true },
    { key: 'phoneNumberId', label: 'Phone Number ID', type: 'text', placeholder: '1234567890', required: true },
    { key: 'apiVersion', label: 'API Version', type: 'text', placeholder: 'v18.0', required: true },
    { key: 'verifyToken', label: 'Verify Token', type: 'password', placeholder: 'my-verify-token', required: true },
    { key: 'appSecret', label: 'App Secret', type: 'password', placeholder: 'Tu App Secret', required: true },
  ],
  TWILIO_WHATSAPP: [
    { key: 'accountSid', label: 'Account SID', type: 'text', placeholder: 'ACxxxxxx', required: true },
    { key: 'authToken', label: 'Auth Token', type: 'password', placeholder: 'Tu auth token', required: true },
    { key: 'fromNumber', label: 'From Number', type: 'text', placeholder: '+14155238886', required: true },
  ],
  // Push Provider
  FIREBASE: [
    { key: 'projectId', label: 'Project ID', type: 'text', placeholder: 'my-project-id', required: true },
    { key: 'clientEmail', label: 'Client Email', type: 'text', placeholder: 'service@project.iam.gserviceaccount.com', required: true },
    { key: 'privateKey', label: 'Private Key', type: 'password', placeholder: '-----BEGIN PRIVATE KEY-----...', required: true },
  ],
  // Payment Providers
  STRIPE: [
    { key: 'secretKey', label: 'Secret Key', type: 'password', placeholder: 'sk_live_xxxxxxxx', required: true, helpText: 'Dashboard de Stripe → Developers → API keys → Secret key. Usa sk_test_ para pruebas.' },
    { key: 'publishableKey', label: 'Publishable Key', type: 'text', placeholder: 'pk_live_xxxxxxxx', required: true, helpText: 'Dashboard de Stripe → Developers → API keys → Publishable key. Esta clave es pública.' },
    { key: 'webhookSecret', label: 'Webhook Secret', type: 'password', placeholder: 'whsec_xxxxxxxx', required: true, helpText: 'Stripe → Developers → Webhooks → Crear endpoint con URL: https://hommi.mxv1/payments/gateway/webhook/stripe. El "Signing secret" (whsec_...) es este campo.' },
  ],
  MERCADOPAGO: [
    { key: 'accessToken', label: 'Access Token', type: 'password', placeholder: 'APP_USR-xxxxxxxx', required: true, helpText: 'Panel de MercadoPago → Tu negocio → Configuración → Credenciales → Access Token de producción o sandbox.' },
    { key: 'publicKey', label: 'Public Key', type: 'text', placeholder: 'APP_USR-xxxxxxxx', required: true, helpText: 'Panel de MercadoPago → Tu negocio → Configuración → Credenciales → Public Key.' },
    { key: 'webhookSecret', label: 'Webhook Secret', type: 'password', placeholder: 'Tu secret key', required: false, helpText: 'MercadoPago → Tu negocio → Configuración → Webhooks → Crear webhook con URL: https://hommi.mxv1/payments/gateway/webhook/mercadopago' },
  ],
  CONEKTA: [
    { key: 'apiKey', label: 'API Key', type: 'password', placeholder: 'key_xxxxxxxx', required: true, helpText: 'Panel de Conekta → Desarrolladores → API Keys → Private Key.' },
    { key: 'publicKey', label: 'Public Key', type: 'text', placeholder: 'key_xxxxxxxx', required: true, helpText: 'Panel de Conekta → Desarrolladores → API Keys → Public Key.' },
    { key: 'webhookSecret', label: 'Webhook Secret', type: 'password', placeholder: 'Tu webhook signing key', required: false, helpText: 'Conekta → Desarrolladores → Webhooks → Crear con URL: https://hommi.mxv1/payments/gateway/webhook/conekta. Copia el Signing Key.' },
  ],
  // Access Control Providers
  HIKCENTRAL: [
    { key: 'baseUrl', label: 'Base URL', type: 'text', placeholder: 'https://hikcentral.example.com:443', required: true, helpText: 'URL del servidor HikCentral incluyendo el puerto' },
    { key: 'appKey', label: 'App Key', type: 'password', placeholder: 'Your Artemis App Key', required: true, helpText: 'Artemis API App Key de HikCentral' },
    { key: 'appSecret', label: 'App Secret', type: 'password', placeholder: 'Your Artemis App Secret', required: true, helpText: 'Artemis API App Secret de HikCentral' },
    { key: 'bumblebeeUsername', label: 'Web Portal Username', type: 'text', placeholder: 'admin', required: true, helpText: 'Usuario del portal web de HikCentral — requerido para sincronización de PINs a dispositivos' },
    { key: 'bumblebeePassword', label: 'Web Portal Password', type: 'password', placeholder: '', required: true, helpText: 'Contraseña del portal web de HikCentral — requerido para sincronización de PINs a dispositivos' },
  ],
};

export const typeInfo: Record<IntegrationType, { label: string; description: string; icon: string }> = {
  EMAIL: {
    label: 'Email',
    description: 'Envío de correos electrónicos transaccionales y marketing',
    icon: 'mail',
  },
  SMS: {
    label: 'SMS',
    description: 'Mensajes de texto para notificaciones y alertas',
    icon: 'smartphone',
  },
  WHATSAPP: {
    label: 'WhatsApp',
    description: 'Mensajes de WhatsApp Business para comunicación directa',
    icon: 'message-square',
  },
  PUSH: {
    label: 'Push Notifications',
    description: 'Notificaciones push para aplicaciones móviles y web',
    icon: 'bell',
  },
  PAYMENT: {
    label: 'Pagos',
    description: 'Procesamiento de pagos con tarjeta y otros métodos',
    icon: 'credit-card',
  },
  ACCESS: {
    label: 'Control de Acceso',
    description: 'Control de acceso, videovigilancia y biometría',
    icon: 'camera',
  },
  STORAGE: {
    label: 'Almacenamiento',
    description: 'Almacenamiento de archivos y documentos en la nube',
    icon: 'cloud',
  },
};
